import { useState, useEffect } from "react";
import { X, Check } from "@phosphor-icons/react";

const FilterModal = ({
  isVisible,
  onClose,
  onSave,
  onCancel,
  productFilterData,
  facets,
  currentFilters = [],
}) => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);

  // Initialize selected filters from current filters
  useEffect(() => {
    if (isVisible) {
      const initialFilters = {};
      currentFilters.forEach((filter) => {
        if (filter.type === "PRICE" && filter.valueList?.length > 0) {
          initialFilters[filter.id] = filter.valueList[0];
        } else if (filter.valueList?.length > 0) {
          initialFilters[filter.id] = filter.valueList;
        }
      });
      setSelectedFilters(initialFilters);
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
    }
  }, [isVisible, currentFilters]);

  const handleFilterToggle = (filterId, valueId, isMultiSelect = false) => {
    setSelectedFilters((prev) => {
      const current = prev[filterId] || [];

      if (isMultiSelect) {
        if (Array.isArray(current)) {
          const exists = current.includes(valueId);
          if (exists) {
            return {
              ...prev,
              [filterId]: current.filter((id) => id !== valueId),
            };
          } else {
            return { ...prev, [filterId]: [...current, valueId] };
          }
        } else {
          return { ...prev, [filterId]: [valueId] };
        }
      } else {
        // Single select
        if (
          current === valueId ||
          (Array.isArray(current) && current[0] === valueId)
        ) {
          const newFilters = { ...prev };
          delete newFilters[filterId];
          return newFilters;
        } else {
          return { ...prev, [filterId]: valueId };
        }
      }
    });
  };

  const handleSave = () => {
    const filterList = [];

    Object.entries(selectedFilters).forEach(([filterId, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;

      // Find filter config from productFilterData
      const filterConfig = productFilterData.filters?.find(
        (f) => f.id === filterId
      );

      if (filterConfig) {
        const filter = {
          id: filterId,
          type: filterConfig.type,
          displayType: filterConfig.displayType,
          valueList: Array.isArray(value) ? value : [value],
        };
        filterList.push(filter);
      }
    });

    onSave(filterList);
    onClose();
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  const renderFilterSection = (filter) => {
    const facetData = facets?.find((f) => f.id === filter.id);

    if (
      !facetData &&
      filter.type !== "PRICE" &&
      filter.type !== "DISCOUNT_RATIO"
    ) {
      return null;
    }

    const selectedValues = selectedFilters[filter.id] || [];

    return (
      <div key={filter.id} className="mb-6">
        <h3 className="font-semibold text-lg mb-3 text-gray-800">
          {filter.name}
        </h3>

        {/* Price Range Filter */}
        {filter.type === "PRICE" && filter.displayType === "NUMBER_RANGE" && (
          <div className="space-y-2">
            {filter.customValues?.map((range, index) => {
              const [min, max] = range.split(":");
              const isSelected =
                selectedValues === range ||
                (Array.isArray(selectedValues) && selectedValues[0] === range);

              return (
                <button
                  key={index}
                  onClick={() => handleFilterToggle(filter.id, range, false)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    isSelected
                      ? "bg-black text-white border-black"
                      : "bg-white border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>
                      ₺{min} - ₺{max}
                    </span>
                    {isSelected && <Check size={16} weight="bold" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Discount Range Filter */}
        {filter.type === "DISCOUNT_RATIO" &&
          filter.displayType === "NUMBER_RANGE" && (
            <div className="space-y-2">
              {filter.customValues?.map((range, index) => {
                const [min, max] = range.split(":");
                const isSelected =
                  selectedValues === range ||
                  (Array.isArray(selectedValues) &&
                    selectedValues[0] === range);

                return (
                  <button
                    key={index}
                    onClick={() => handleFilterToggle(filter.id, range, false)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                      isSelected
                        ? "bg-black text-white border-black"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        %{min} - %{max} İndirim
                      </span>
                      {isSelected && <Check size={16} weight="bold" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

        {/* Category and Other Facet Filters */}
        {filter.isFacetFilter && facetData && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {facetData.values.map((facetValue) => {
              let filterValue;

              // For category filter, get the category name from productFilterData
              if (filter.id === "category") {
                filterValue =
                  productFilterData?.data?.getProductFilterData?.categories?.find(
                    (cat) => cat.id === facetValue.id
                  );
              } else {
                filterValue = filter.values?.find(
                  (v) => v.id === facetValue.id
                );
              }

              const isSelected = Array.isArray(selectedValues)
                ? selectedValues.includes(facetValue.id)
                : selectedValues === facetValue.id;

              if (!filterValue) return null;

              return (
                <button
                  key={facetValue.id}
                  onClick={() =>
                    handleFilterToggle(
                      filter.id,
                      facetValue.id,
                      filter.isMultiSelect
                    )
                  }
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    isSelected
                      ? "bg-black text-white border-black"
                      : "bg-white border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{filterValue.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm opacity-70">
                        ({facetValue.count})
                      </span>
                      {isSelected && <Check size={16} weight="bold" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Stock Status and other simple list filters */}
        {filter.type === "STOCK_STATUS" && filter.displayType === "LIST" && (
          <div className="space-y-2">
            {filter.values?.map((value) => {
              const facetValue = facetData?.values?.find(
                (fv) => fv.id === value.id
              );
              const isSelected = Array.isArray(selectedValues)
                ? selectedValues.includes(value.id)
                : selectedValues === value.id;

              return (
                <button
                  key={value.id}
                  onClick={() =>
                    handleFilterToggle(
                      filter.id,
                      value.id,
                      filter.isMultiSelect
                    )
                  }
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    isSelected
                      ? "bg-black text-white border-black"
                      : "bg-white border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>
                      {value.id === "in-stock" ? "Stokta Var" : "Stokta Yok"}
                    </span>
                    <div className="flex items-center gap-2">
                      {facetValue && (
                        <span className="text-sm opacity-70">
                          ({facetValue.count})
                        </span>
                      )}
                      {isSelected && <Check size={16} weight="bold" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black bg-opacity-50">
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] flex flex-col transition-transform duration-300 ${
          modalVisible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="w-6"></div> {/* Spacer */}
          <h2 className="text-lg font-semibold">Filter</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close filter"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {productFilterData?.filters.map((filter) =>
            renderFilterSection(filter)
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-3 px-4 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
